/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useMemo } from 'react';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/styles/makeStyles';
import ChevronRight from '@material-ui/icons/ChevronRight';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { FilterType, InputType } from '../../../components/foTable/foTableFilter/FilterType';
import { SubmissionStatus } from '../../../models/SubmissionStatus';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    margin: '10px',
    border: '1px solid transparent',
    background: theme.palette.elementBackground,
    borderRadius: '4px',
    marginRight: 0,
    display: 'block',
    textDecoration: 'none',
    color: 'inherit',
    '& span:not(:last-child)': {
      fontWeight: 500,
      fontSize: '1.563rem',
      marginRight: '.5rem',
    },
    '& svg': {
      color: theme.palette.primary.main,
    },

    '&[data-focustype="key"]': {
      outline: 'none',
      boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${theme.palette.focus}`,
    },

    '&:focus': {
      outline: 'none',
    },
  },
  statusCardContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '100%',
  },
  textContainer: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    fontSize: '1.5rem',
    fontWeight: '600',
    [theme.breakpoints.up('xl3')]: {
      fontSize: '1.125rem',
    },
  },
  labelText: {
    fontSize: '1rem',
    color: theme.palette.dashboardLabel,
  },
  statusCardHighlight: ({ status }) => ({
    width: '.5rem',
    height: 'inherit',
    borderTopLeftRadius: '.25rem',
    borderBottomLeftRadius: '.25rem',
    background: theme.palette.statusChip[status]?.main,
  }),
}));

export function DashboardCardStatus({ id, total, status, label, navigationUrl }) {
  const classes = useStyles({ status });
  const { t } = useTranslation();

  const statusFilter = useMemo(() => {
    if (!status) return []; // handle null status

    const filterList = [
      {
        type: FilterType.STATUS,
        inputType: InputType.STATUS,
        value: SubmissionStatus[status]?.id,
      },
    ];
    if (status === SubmissionStatus.DRAFT.id) {
      return filterList.concat({
        type: FilterType.STATUS,
        inputType: InputType.STATUS,
        value: SubmissionStatus.VALID_DRAFT.id,
      });
    }
    if (status === SubmissionStatus.READY_TO_SIGN.id) {
      return filterList.concat({
        type: FilterType.STATUS,
        inputType: InputType.STATUS,
        value: SubmissionStatus.VALID_READY_TO_SIGN.id,
      });
    }
    return filterList;
  }, [status]);

  return (
    <Link className={classes.root} id={id} to={{ pathname: navigationUrl, state: { filter: statusFilter } }}>
      <div className={classes.statusCardContent}>
        <Typography className={classes.textContainer}>
          <span className={classNames(classes.statusCardHighlight)} aria-hidden="true" />
          <span>
            {total}
            {' '}
          </span>
          <span className={classes.labelText}>{t(label)}</span>
        </Typography>
        {total > 0 && <ChevronRight />}
      </div>
    </Link>
  );
}
